import React, { useEffect } from "react";
import { Separator } from "../../ui/separator";

const AIUse = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="flex-grow bg-white py-8 md:py-12">
      <div className="container mx-auto px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl font-bold mb-6 text-center text-[#2D0052]">
              AI Transparency & Automated Decision-Making Disclosure
            </h1>
            <p className="text-sm text-gray-500 text-center mb-8">
              Last Updated: Jun 02, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                1. Introduction
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                Oye Creators ("we," "us," "our"), operated by TTL Media
                Private Limited, values transparency and accountability in all
                automated or AI-driven processes. This Disclosure explains our
                use of automated decision-making ("ADM"), including profiling
                and AI algorithms. It also outlines your rights and how to
                exercise them under international and Indian data protection
                laws.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                2. Who This Applies To
              </h2>
              <p className="mb-4">
                This Disclosure covers all users of our marketplace platform:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Brands & Businesses posting Gigs to onboard Creators
                  ("Brands")
                </li>
                <li>Creators completing confirmed Gigs ("Creators")</li>
                <li>
                  Platform Administrators and Oye Creators personnel
                  ("Admins")
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                3. Key Terms & Legal Context
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>Automated Decision-Making (ADM):</strong> Decisions
                  made by algorithms or AI with minimal or no human
                  intervention.
                </li>
                <li>
                  <strong>Profiling:</strong> Automated analysis of personal
                  data to evaluate attributes such as performance, behavior,
                  or preferences.
                </li>
                <li>
                  <strong>Significant Effects:</strong> Any decision impacting
                  contract eligibility, ranking, scoring, or legal rights.
                </li>
              </ul>

              <p className="mt-4 mb-2">
                <strong>Legal Framework:</strong>
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>EU GDPR Art. 22:</strong> Right not to be subject
                  solely to automated decisions with legal or significant
                  effects without explicit consent or contract necessity.
                </li>
                <li>
                  <strong>
                    India's Personal Data Protection Act, 2023 (PDPA):
                  </strong>{" "}
                  Rights to human intervention, correction, and objection to
                  automated processing.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                4. Our Automated Processes
              </h2>
              <p className="mb-4">
                <strong>4.1 Matching & Recommendation</strong>
              </p>
              <p className="mb-4">
                We analyze Creator profiles (skills, location, past project
                performance) to suggest relevant Gigs, and likewise recommend
                Creators to Brands based on campaign requirements.
              </p>

              <p className="mb-4">
                <strong>4.2 Engagement & Performance Scoring</strong>
              </p>
              <p className="mb-4">
                Creators receive an Engagement Score derived from completion
                rates, on-time delivery, audience interactions (likes, shares,
                comments), and Brand feedback. Scores are updated weekly.
              </p>

              <p className="mb-4">
                <strong>4.3 Fraud Detection & Risk Management</strong>
              </p>
              <p className="mb-4">
                AI models scan for suspicious activities—such as unusual
                spikes in engagement or patterns indicative of bot behavior—to
                protect platform integrity.
              </p>

              <p className="mb-4">
                <strong>4.4 Resource Allocation for Admins</strong>
              </p>
              <p className="mb-4">
                Internally, we use algorithms to distribute workload among
                Admins (e.g., support tickets, campaign audits) to maintain
                balanced assignments.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                5. How Decisions Are Made
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>Data Inputs:</strong> Personal profile information,
                  Gig history, engagement metrics, user classifications.
                </li>
                <li>
                  <strong>Model Training & Validation:</strong> AI models are
                  trained on historical data and updated quarterly. We conduct
                  bias checks and accuracy audits before each release.
                </li>
                <li>
                  <strong>Thresholds & Overrides:</strong> Predefined
                  thresholds trigger automated actions (e.g., scoring,
                  flagging). Human review is available for all significant
                  outcomes.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                6. Purpose & Benefits
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>Efficiency:</strong> Faster Gig matching and
                  onboarding.
                </li>
                <li>
                  <strong>Fairness:</strong> Objective scoring reduces
                  subjective bias.
                </li>
                <li>
                  <strong>Security:</strong> Proactive detection of fraudulent
                  behavior.
                </li>
                <li>
                  <strong>Scalability:</strong> Automated tools allow our
                  small team to manage a growing user base.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                7. Potential Limitations & Risks
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>Accuracy Limitations:</strong> No algorithm is
                  perfect; false positives/negatives can occur.
                </li>
                <li>
                  <strong>Data Quality:</strong> Model performance depends on
                  the accuracy of input data.
                </li>
                <li>
                  <strong>Liability Exclusion:</strong> Oye Creators does not
                  guarantee outcomes of third-party decisions by Brands using
                  our scores or recommendations.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                8. Your Rights & How to Exercise Them
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Right
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Description
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        How to Exercise
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Access
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Obtain details on automated processing and logic.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Email dpo@oyecreators.com with subject "Request ADM
                        Details."
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Human Review
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Request a human to review or override an ADM outcome.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        In Dashboard → Support → "Request Human Review."
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Correction
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Rectify inaccurate personal data used in ADM.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Profile Settings → Update Information, or email
                        dpo@oyecreators.com.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Object & Opt-Out
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Object to profiling or certain automated decisions.
                        May limit features.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Account Settings → Privacy & Data → Manage Profiling,
                        or email privacy@oyecreators.com.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Consent Withdrawal
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Withdraw previously given consent for ADM.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Email privacy@oyecreators.com with "Withdraw ADM
                        Consent."
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                9. Process for Requests & Appeals
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>Acknowledge receipt within 5 business days.</li>
                <li>
                  Provide decision and, if applicable, take corrective action
                  within 30 calendar days.
                </li>
                <li>
                  If unsatisfied, escalate to the Chief Compliance Officer at
                  cco@oyecreators.com.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                10. Data Retention & Security
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Profiling data (scores, logs) is retained for up to 2 years,
                  then anonymized.
                </li>
                <li>
                  All data is stored in encrypted form (AES-256 at rest; TLS
                  1.2+ in transit).
                </li>
                <li>
                  Regular security audits and third-party penetration testing
                  ensure robustness.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                11. Indemnification & Liability
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>Brands:</strong> Oye Creators is not responsible for
                  hiring decisions you make based on our recommendations.
                </li>
                <li>
                  <strong>Creators:</strong> We do not control Brand selection
                  choices; our scores are advisory only.
                </li>
                <li>
                  <strong>Platform & Employees:</strong> Protected from claims
                  arising solely from algorithmic decisions, except in cases
                  of willful misconduct.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                12. Updates & Notifications
              </h2>
              <p className="mb-4">
                We review this Disclosure annually or upon substantial
                changes. Updates will appear with a new "Last Updated" date,
                and users will be notified via email if changes are material.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#D9008D]">
                13. Contact Information
              </h2>
              <p className="mb-1">
                Data Protection Officer: dpo@oyecreators.com
              </p>
              <p className="mb-1">
                Grievance Officer: grievance@oyecreators.com
              </p>
              <p className="mb-1">
                Registered Office: TTL Media Private Limited, [Address], Pune,
                Maharashtra, India
              </p>
              <p className="mt-4">End of Disclosure</p>
            </section>
          </div>
        </div>
      </div>
    </main>
  )
}

export default AIUse
